/**
 * Компонент ребра (линии связи) между коммитами
 * Отображает связь между двумя коммитами с кривой Безье
 */

import { memo } from 'react';
import { cn } from '@/lib/utils';
import type { CommitEdge as CommitEdgeType, GraphConfig } from './types';
import { DEFAULT_GRAPH_CONFIG } from './types';
import { generateEdgePath } from './GraphLayout';

interface CommitEdgeProps {
  edge: CommitEdgeType;
  config?: Partial<GraphConfig>;
  isNew?: boolean;
  isAnimating?: boolean;
}

/**
 * Компонент линии связи между коммитами
 */
export const CommitEdge = memo(function CommitEdge({
  edge,
  config,
  isNew = false,
  isAnimating = false,
}: CommitEdgeProps) {
  const fullConfig: GraphConfig = { ...DEFAULT_GRAPH_CONFIG, ...config };
  const { fromPosition, toPosition, color, isMerge } = edge;

  // Генерируем путь SVG
  const path = generateEdgePath(fromPosition, toPosition, isMerge);

  return (
    <g className="commit-edge">
      {/* Тень для глубины */}
      <path
        d={path}
        fill="none"
        stroke="rgba(0,0,0,0.3)"
        strokeWidth={fullConfig.edgeWidth + 2}
        strokeLinecap="round"
        transform="translate(1, 1)"
        className="opacity-50"
      />

      {/* Основная линия */}
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={fullConfig.edgeWidth}
        strokeLinecap="round"
        className={cn(
          'transition-all duration-300',
          isNew && 'animate-edge-draw',
          isAnimating && 'animate-edge-pulse'
        )}
        style={{
          // Для анимации рисования линии
          strokeDasharray: isNew ? '1000' : 'none',
          strokeDashoffset: isNew ? '0' : '0',
        }}
      />

      {/* Анимированный градиент для активных операций */}
      {isAnimating && (
        <path
          d={path}
          fill="none"
          stroke="url(#edge-gradient)"
          strokeWidth={fullConfig.edgeWidth}
          strokeLinecap="round"
          className="animate-edge-flow"
        />
      )}
    </g>
  );
});

/**
 * SVG определения для градиентов и фильтров
 */
export function EdgeDefs() {
  return (
    <defs>
      {/* Градиент для анимированных рёбер */}
      <linearGradient id="edge-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="rgba(255,255,255,0.8)">
          <animate attributeName="offset" values="0;1;0" dur="1.5s" repeatCount="indefinite" />
        </stop>
        <stop offset="50%" stopColor="rgba(255,255,255,0)">
          <animate
            attributeName="offset"
            values="0.5;1.5;0.5"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </stop>
      </linearGradient>

      {/* Фильтр свечения */}
      <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>

      {/* Фильтр тени */}
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="1" dy="1" stdDeviation="1" floodOpacity="0.3" />
      </filter>
    </defs>
  );
}
